import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { siteConfig, waLink } from '../config/site';
import { Logo } from './Logo';

const navItems = [
  { to: '/', key: 'home' },
  { to: '/destinations', key: 'destinations' },
  { to: '/private-trips', key: 'privateTrips' },
  { to: '/journal', key: 'journal' },
  { to: '/plan', key: 'planMyTrip' },
  { to: '/contact', key: 'contact' },
];

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-white/20 bg-ivory/80 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8">
        <Link to="/" data-testid="header-logo-link" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `text-[11px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? 'text-gold-dark' : 'text-navy/70 hover:text-navy'
                }`
              }
            >
              {t.nav[item.key]}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div className="flex items-center border border-navy/15 text-[11px] font-semibold tracking-widest" data-testid="language-switcher">
            <button
              data-testid="lang-id-button"
              onClick={() => setLang('id')}
              className={`px-3 py-1.5 transition-colors duration-300 ${lang === 'id' ? 'bg-navy text-ivory' : 'text-navy/60 hover:text-navy'}`}
            >
              ID
            </button>
            <button
              data-testid="lang-en-button"
              onClick={() => setLang('en')}
              className={`px-3 py-1.5 transition-colors duration-300 ${lang === 'en' ? 'bg-navy text-ivory' : 'text-navy/60 hover:text-navy'}`}
            >
              EN
            </button>
          </div>
          <a
            href={waLink(t.whatsappDefault)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="group flex items-center gap-2 bg-gold px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors duration-300 hover:bg-gold-light"
          >
            <MessageCircle className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
            {t.whatsappCta}
          </a>
        </div>

        <button
          className="text-navy lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-navy/10 bg-ivory lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-6">
              {navItems.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.key}`}
                  className={({ isActive }) =>
                    `border-b border-navy/5 py-4 font-heading text-xl ${isActive ? 'text-gold-dark' : 'text-navy'}`
                  }
                >
                  {t.nav[item.key]}
                </NavLink>
              ))}
              <div className="flex items-center justify-between pt-6">
                <div className="flex items-center border border-navy/15 text-xs font-semibold tracking-widest">
                  <button
                    data-testid="mobile-lang-id-button"
                    onClick={() => setLang('id')}
                    className={`px-4 py-2 ${lang === 'id' ? 'bg-navy text-ivory' : 'text-navy/60'}`}
                  >
                    ID
                  </button>
                  <button
                    data-testid="mobile-lang-en-button"
                    onClick={() => setLang('en')}
                    className={`px-4 py-2 ${lang === 'en' ? 'bg-navy text-ivory' : 'text-navy/60'}`}
                  >
                    EN
                  </button>
                </div>
                <a
                  href={waLink(t.whatsappDefault)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-button"
                  className="flex items-center gap-2 bg-gold px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
